import React, { useRef, useState } from "react";

import { Tooltip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import ShipeeziGrid, { Constants } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";

import BadgeSubStatus from "../../../../../../../../../components/Generic/BadgeSubStatus";
import BadgeType from "../../../../../../../../../components/Generic/BadgeType";
import { DefaultTooltip } from "../../../../../../../../../components/Generic/Tooltip";
import { PropHandles } from "../../../../../../../../../components/Generic/ViewJobComponent";
import { SidebarViewOrder } from "../../../../../../../../../components/SidebarViewOrder";
import { useAppState } from "../../../../../../../../../context/StateContext";
import { Customer } from "../../../../../../../../../models/customer";
import { Job, JobLocation } from "../../../../../../../../../models/job";
import { JobType } from "../../../../../../../../../models/jobType";
import { Location } from "../../../../../../../../../models/location";
import { Filters, Searches, Sort } from "../../../../../../../../../models/shipeezi.table";
import { Statuses } from "../../../../../../../../../models/statuses";
import JobService from "../../../../../../../../../services/job.service";
import JobTypeService from "../../../../../../../../../services/job.type.service";
import StatusesService from "../../../../../../../../../services/statuses.service";
import { calculateTotalVolume } from "../../../../../../../../../util/calculateTotalVolume";
import { CACHE_TIME, STALE_TIME } from "../../../../../../../../../util/constants";
import { handleGetConvertValue, Metric } from "../../../../../../../../../util/converts";
import { ItemJobSubStatus, Status, SubStatusUsedBy } from "../../../../../../../../../util/enums";
import { formatJobLocation } from "../../../../../../../../../util/formatItemLocation";
import { getMeasurement } from "../../../../../../../../../util/getMeasurement";
import { getOrderCustomerDisplayName } from "../../../../../../../../../util/names";
import { handlesGrid } from "../../../../../../../../../util/handlesGrid";
import { FiEyeButton } from "../../../../../Configurations/NotifyParty/styles";
import { useEquipmentContext } from "../../context/Equipment";
import { ContainerLocation, NoDataContainer, NoDataTitle } from "./styles";

export const Orders = () => {
  const {
    equipment: { id: equipmentId },
  } = useEquipmentContext();
  const { currentUser } = useAppState();

  const { t: translate } = useTranslation();

  const [disableCustomerSorting, setDisableCustomerSorting] = useState<boolean>(false);
  const [disableOriginSorting, setDisableOriginSorting] = useState<boolean>(false);
  const [disableDestinationSorting, setDisableDestinationSorting] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState<Filters>();
  const [searches, setSearches] = useState<Searches>([]);
  const [sorting, setSorting] = useState<Array<string[]>>();
  const [jobTypes, setJobTypes] = useState<JobType[]>([]);
  const [jobStatus, setJobStatus] = useState<Statuses[]>([]);
  const [toViewOrder, setToViewOrder] = useState<Job>();
  const sideViewRef = useRef<PropHandles>(null);

  const measurement = getMeasurement();
  const { handleCurrentPage, handlePageSize, handleSort, handleFilter } = handlesGrid({
    setFilters,
    setSorting,
    setPageSize,
    setPage,
  });

  const handleSearch = (values: Searches) => {
    if (setSearches) setSearches(values);
    if (setPage) setPage(0);

    return values;
  };

  const showBadgeType = (value?: string) => {
    return <BadgeType value={value} />;
  };

  const showBadgeSubStatus = (value?: string) => {
    return <BadgeSubStatus value={value} />;
  };

  const renderTextLocation = (locations?: Location[]) => {
    if (locations && locations?.length > 0)
      return locations[0]?.longAddress?.toString()?.trim() || "";

    return "";
  };

  const renderGridLoadingStatus = () => {
    if (loading) {
      return `${translate("loadingQuotes")}...`;
    }

    return (
      <NoDataContainer>
        <NoDataTitle>{translate("noInformationToBeShown")}</NoDataTitle>
      </NoDataContainer>
    );
  };

  const showLocations = (jobLocations: JobLocation) => {
    if (jobLocations) {
      const uniques = formatJobLocation(jobLocations);

      const renderLocations = (location: string) => <div key={location}>{location}</div>;

      const tooltipSpread = uniques.map((location) => {
        return renderLocations(location);
      });

      return uniques && uniques?.length ? (
        <DefaultTooltip title={tooltipSpread} placement="top-start">
          <div>
            {uniques.map((location) => {
              return location !== "" ? renderLocations(location) : <></>;
            })}
          </div>
        </DefaultTooltip>
      ) : (
        <div>-</div>
      );
    }

    return "";
  };

  const renderStatusOfData = () => {
    return renderGridLoadingStatus();
  };

  const renderOrigin = (locations?: Location[]) => {
    return (
      <ContainerLocation>
        <div className="flex-location">
          <div className="div-location-ellipsis">
            <span>{renderTextLocation(locations)}</span>
          </div>
        </div>
      </ContainerLocation>
    );
  };

  const renderDestination = (locations?: Location[]) => {
    return (
      <ContainerLocation>
        <div className="flex-location">
          <div className="div-location-ellipsis">
            <span>{renderTextLocation(locations)}</span>
          </div>
        </div>
      </ContainerLocation>
    );
  };

  const handleViewButton = (id: string | number, order: Job) => {
    return (
      <Tooltip data-cy="action-view" title={translate("view")} placement="top">
        <div>
          <FiEyeButton
            onClick={() => {
              setToViewOrder(order);
              sideViewRef.current?.setOpenSideBar(true);
            }}
            color="#475467"
            size={Constants.defaultGeneralActionsIconSize}
          />
        </div>
      </Tooltip>
    );
  };

  useQuery(
    ["jobType.list"],
    () =>
      JobTypeService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name", "asc"]]),
      }),
    {
      refetchOnWindowFocus: false,
      enabled: !!currentUser?.companyId,
      onSuccess: (response) => {
        if (response && response.data && response.data.length > 0) setJobTypes(response.data);
      },
    }
  );

  useQuery(
    ["statuses.list.order"],
    () =>
      StatusesService.getAll({
        status: Status.ACTIVE,
        companyId: currentUser?.companyId,
        usedBy: SubStatusUsedBy.ORDERS,
      }),
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data.length > 0) {
          setJobStatus(response.data);
        }
      },
    }
  );

  const { data: orders, isFetching: loading } = useQuery(
    ["orders.by.equipment.list", page, pageSize, sorting, filters, searches],
    async () => {
      const response = await JobService.getAll({
        attributes: JSON.stringify([
          "id",
          "reference",
          "type",
          "mode",
          "volume",
          "updatedAt",
          "createdAt",
          "customVolumeActual",
          "status",
          "leadSourceFrom",
        ]),
        companyId: currentUser?.companyId,
        page,
        pageSize,
        sortBy:
          sorting && sorting?.length > 0
            ? JSON.stringify(sorting)
            : JSON.stringify([["updatedAt", "DESC"]]),
        include: JSON.stringify([
          {
            required: true,
            association: "tasks",
            attributes: [],
            include: [
              {
                required: true,
                association: "fleets",
                attributes: [],
                where: {
                  id: equipmentId,
                },
              },
            ],
          },
          {
            association: "origins",
            required: false,
            attributes: ["id", "longAddress", "createdAt"],
            order: ["createdAt", "desc"],
            through: {
              attributes: [],
            },
          },
          {
            required: false,
            association: "destinations",
            attributes: ["id", "longAddress", "createdAt"],
            order: ["createdAt", "desc"],
            through: {
              attributes: [],
            },
          },
          {
            association: "customer",
            required: false,
            attributes: ["id", "fullName"],
          },
          {
            association: "itemsJob",
            attributes: ["id", "quantity", "volume", "status", "subStatus"],
            include: [
              { association: "destination" },
              {
                association: "mapCells",
                attributes: ["id", "name", "mapId"],
                include: [
                  {
                    association: "map",
                    attributes: ["id", "warehouseId", "sectionId", "name"],
                    include: [
                      { association: "warehouse", attributes: ["id", "name"] },
                      { association: "section", attributes: ["id", "name"] },
                    ],
                  },
                ],
              },
            ],
          },
          {
            association: "storageBoxes",
            attributes: ["id", "number", "storageBoxTypeId"],
            include: [
              {
                association: "storageBoxType",
                attributes: ["id", "name"],
              },
            ],
          },
          {
            association: "materials",
            attributes: ["id", "volume", "quantity"],
            include: [
              {
                association: "serviceBooking",
                attributes: ["id", "isItem"],
              },
              {
                association: "destination",
                attributes: ["id", "name"],
              },
            ],
          },
        ]),
        searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      });

      if (response?.data) return response?.data as Job[];

      return [];
    },
    {
      enabled: !!currentUser?.id && !!equipmentId,
      refetchOnWindowFocus: false,
      staleTime: STALE_TIME,
      cacheTime: CACHE_TIME,
    }
  );

  const { data: total } = useQuery(
    ["orders.by.equipment.count", filters, searches],
    async () => {
      const response = await JobService.count({
        companyId: currentUser?.companyId,
        include: JSON.stringify([
          {
            required: true,
            association: "tasks",
            attributes: [],
            include: [
              {
                required: true,
                association: "fleets",
                attributes: [],
                where: {
                  id: equipmentId,
                },
              },
            ],
          },
          {
            association: "origins",
            required: false,
            attributes: ["id", "longAddress", "createdAt"],
            order: ["createdAt", "desc"],
            through: {
              attributes: [],
            },
          },
          {
            required: false,
            association: "destinations",
            attributes: ["id", "longAddress", "createdAt"],
            order: ["createdAt", "desc"],
            through: {
              attributes: [],
            },
          },
          {
            association: "customer",
            required: false,
            attributes: ["id", "fullName"],
          },
        ]),
        searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      });

      return response?.data || 0;
    },
    {
      enabled: !!currentUser?.id && !!equipmentId,
      staleTime: STALE_TIME,
      cacheTime: CACHE_TIME,
    }
  );

  const { data: totalWithoutFilter } = useQuery(
    ["orders.by.equipment.count.total.without.filter"],
    async () => {
      const response = await JobService.count({
        companyId: currentUser?.companyId,
        include: JSON.stringify([
          {
            required: true,
            association: "tasks",
            attributes: [],
            include: [
              {
                required: true,
                association: "fleets",
                attributes: [],
                where: {
                  id: equipmentId,
                },
              },
            ],
          },
        ]),
      });

      return response?.data || 0;
    },
    {
      enabled: !!currentUser?.id && !!equipmentId,
      staleTime: STALE_TIME,
      cacheTime: CACHE_TIME,
    }
  );

  return (
    <>
      <ShipeeziGrid
        columns={[
          {
            name: "type",
            title: translate("type"),
            formatComponent: showBadgeType,
            hideSearch: true,
          },
          {
            name: "mode",
            title: translate("mode"),
            hideSearch: true,
            formatComponent: (value: string) =>
              value ? translate(value.toLowerCase()) : "",
          },
          { name: "reference", title: translate("reference") },
          {
            name: "customer",
            title: translate("customer"),
            hideSort: disableCustomerSorting,
            formatComponent: (_customer: Customer, job: Job) =>
              getOrderCustomerDisplayName(job),
            associationColumnSettings: {
              whenSearching: {
                clearSorting: setSorting,
                disableSorting: setDisableCustomerSorting,
              },
              columnToSorting: "full_name",
            },
          },
          {
            name: "volume",
            title: translate("volume"),
            hideSort: true,
            hideSearch: true,
            formatComponent: (_, job: Job) => {
              if (job?.customVolumeActual === null || job?.customVolumeActual === undefined)
                return calculateTotalVolume(
                  ItemJobSubStatus.ACTUAL,
                  measurement,
                  job?.itemsJob,
                  job?.materials
                );

              return `${handleGetConvertValue(
                job?.customVolumeActual || 0,
                measurement?.code,
                measurement?.decimalPlaces
              )} ${measurement?.code || Metric["m³"]}`.trim();
            },
          },
          {
            name: "origins",
            title: translate("origin"),
            hideSort: disableOriginSorting,
            formatComponent: renderOrigin,
            associationColumnSettings: {
              whenSearching: {
                clearSorting: setSorting,
                disableSorting: setDisableOriginSorting,
              },
              columnToSorting: "long_address",
            },
          },
          {
            name: "destinations",
            title: translate("destination"),
            hideSort: disableDestinationSorting,
            formatComponent: renderDestination,
            associationColumnSettings: {
              whenSearching: {
                clearSorting: setSorting,
                disableSorting: setDisableDestinationSorting,
              },
              columnToSorting: "long_address",
            },
          },
          {
            name: "jobLocations",
            title: translate("location"),
            formatComponent: showLocations,
            hideSort: true,
            hideSearch: true,
          },
          {
            name: "status",
            title: translate("status"),
            formatComponent: showBadgeSubStatus,
            hideSearch: true,
          },
        ]}
        data={orders || []}
        totalItems={total}
        loadingData={loading}
        namePage={translate("orders")}
        emptyData={renderStatusOfData}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        onChangePageSize={handlePageSize}
        currentPage={page}
        onNextPage={handleCurrentPage}
        onPreviousPage={handleCurrentPage}
        onGoToPage={handleCurrentPage}
        generalActions={{
          orderBy:
            totalWithoutFilter && totalWithoutFilter > 0
              ? { left: ["handleSearch", "handleFilterBy"] }
              : {},
          handleSearch: {
            onSearch: (filters) => handleSearch(filters),
          },
          handleFilterBy: {
            onChange: handleFilter,
            categories: [
              {
                name: translate("type"),
                options: jobTypes && jobTypes?.map((jobType) => jobType?.name || ""),
              },
              {
                name: translate("status"),
                options: jobStatus?.map((jobStatus) => jobStatus?.name || ""),
              },
            ],
          },
        }}
        positionActionsColumn="right"
        rowActionsExtra={[
          {
            title: translate("view"),
            key: "view",
            buttonAndAction: handleViewButton,
          },
        ]}
        onSorting={(sorts: Sort[]) => handleSort(sorts)}
      />
      <SidebarViewOrder
        jobId={toViewOrder?.id}
        sideViewRef={sideViewRef}
        onCloseModal={() => setToViewOrder(undefined)}
      />
    </>
  );
};
